import React, { useState } from 'react'

import cn from 'classnames'

import Card from '@src/components/Card'
import { ILeaderboardDataResponse } from '@src/types/point.api'

type Props = {
    account?: string | null
    loading: boolean
    leaderboard: ILeaderboardDataResponse[]
}

export const Leaderboard: React.FC<Props> = ({ account, loading, leaderboard }) => {
    const [currentPage, setCurrentPage] = useState(1)
    const itemsPerPage = 15

    const sortedLeaderboard = leaderboard
        .sort((a, b) => b.total_user_points - a.total_user_points)
        .map((item, index) => ({
            ...item,
            rank: index + 1,
        }))

    // If account exists, find and move it to the top while preserving original rank
    const userIndex = account ? sortedLeaderboard.findIndex(item => item.wallet === account) : -1
    if (userIndex > 0) {
        const userItem = sortedLeaderboard.splice(userIndex, 1)[0]
        // Keep the original rank when moving to front
        const originalRank = userItem.rank
        sortedLeaderboard.unshift({
            ...userItem,
            rank: originalRank, // Preserve the original rank
        })
    }

    return (
        <Card>
            <div className='flex h-full flex-col justify-between'>
                <div>
                    <div className='relative'>
                        <h2 className='pb-4 text-center text-3xl font-bold'>Leaderboard</h2>
                    </div>
                    {loading ? (
                        <img src='/gifs/sandwatch-loading.gif' alt='status' className='m-auto h-40' />
                    ) : (
                        <div className='flex flex-col items-center rounded-lg bg-black/25 p-6'>
                            <table className='w-full'>
                                <thead>
                                    <tr>
                                        <th>Rank</th>
                                        <th>Wallet</th>
                                        <th>Miles</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedLeaderboard
                                        .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                                        .map(board => (
                                            <tr
                                                className={cn(
                                                    'text-center',
                                                    board.wallet === account ? 'text-purple-600' : 'text-white'
                                                )}
                                                key={board.wallet}
                                            >
                                                <td>{board.rank}</td>
                                                <td>
                                                    {board.wallet.substring(0, 7)}...
                                                    {board.wallet.substring(board.wallet.length - 5)}
                                                </td>
                                                <td>{board.total_user_points.toLocaleString()}</td>
                                            </tr>
                                        ))}
                                </tbody>
                            </table>

                            <div className='mt-4 flex items-center justify-center gap-2'>
                                <button
                                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                    disabled={currentPage === 1}
                                    className='rounded bg-button-color p-2 disabled:opacity-50'
                                >
                                    <svg
                                        xmlns='http://www.w3.org/2000/svg'
                                        width='16'
                                        height='16'
                                        fill='currentColor'
                                        viewBox='0 0 16 16'
                                    >
                                        <path
                                            fillRule='evenodd'
                                            d='M11.354 1.646a.5.5 0 0 1 0 .708L5.707 8l5.647 5.646a.5.5 0 0 1-.708.708l-6-6a.5.5 0 0 1 0-.708l6-6a.5.5 0 0 1 .708 0z'
                                        />
                                    </svg>
                                </button>
                                <span className='mx-2'>
                                    Page {currentPage} of {Math.ceil(sortedLeaderboard.length / itemsPerPage)}
                                </span>
                                <button
                                    onClick={() =>
                                        setCurrentPage(p =>
                                            Math.min(Math.ceil(sortedLeaderboard.length / itemsPerPage), p + 1)
                                        )
                                    }
                                    disabled={currentPage >= Math.ceil(sortedLeaderboard.length / itemsPerPage)}
                                    className='rounded bg-button-color p-2 disabled:opacity-50'
                                >
                                    <svg
                                        xmlns='http://www.w3.org/2000/svg'
                                        width='16'
                                        height='16'
                                        fill='currentColor'
                                        viewBox='0 0 16 16'
                                    >
                                        <path
                                            fillRule='evenodd'
                                            d='M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708z'
                                        />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </Card>
    )
}
